import {
  joinTournamentMatch,
  leaveTournament,
  loadTournamentDetail,
  loadTournamentLists,
  registerTournament,
} from "./api";
import { renderBracketGrid } from "./bracket-ui";
import { formatTournamentCountdown, msUntil } from "./countdown";
import { tickTournamentToasts } from "./toast";
import type { TournamentDetail, TournamentSummary } from "./types";

export type TournamentsScreenApi = {
  refresh: () => Promise<void>;
  openDetail: (id: string) => Promise<void>;
  backToList: () => void;
  /** true if handled (e.g. closed detail). */
  handleReturn: () => boolean;
  isVisible: () => boolean;
};

export type TournamentsScreenOpts = {
  isSignedIn: () => boolean;
  getViewerUserId?: () => string | null;
  onNeedSignIn: () => void;
  onPlayMatch: (detail: TournamentDetail, matchId: string) => void;
  showScreen: () => void;
  hideScreen: () => void;
};

type MeStats = {
  registered: TournamentSummary[];
  dotsEarned: number;
  tournamentsWon: number;
};

let flowOpts: TournamentsScreenOpts | null = null;
let screenApi: TournamentsScreenApi | null = null;

export function openTournamentsFlow(): void {
  if (!flowOpts) return;
  flowOpts.showScreen();
  void screenApi?.refresh();
}

export function returnToTournamentScreen(): void {
  flowOpts?.showScreen();
  void screenApi?.refresh();
}

export function handleTournamentsReturn(): boolean {
  return screenApi?.handleReturn() ?? false;
}

export function mountTournamentsScreen(
  root: HTMLElement,
  opts: TournamentsScreenOpts,
): TournamentsScreenApi {
  flowOpts = opts;
  let lists: {
    upcoming: TournamentSummary[];
    active: TournamentSummary[];
    ended: TournamentSummary[];
  } = { upcoming: [], active: [], ended: [] };
  let me: MeStats = { registered: [], dotsEarned: 0, tournamentsWon: 0 };
  let serverNow = new Date().toISOString();
  let detail: TournamentDetail | null = null;
  let countdownTimer: ReturnType<typeof setInterval> | null = null;
  let pollTimer: ReturnType<typeof setInterval> | null = null;

  const shell = document.createElement("div");
  shell.className = "tournamentsScreenInner";

  const heading = document.createElement("h1");
  heading.className = "tournamentsHeading";
  heading.textContent = "TOURNAMENTS";

  const hint = document.createElement("p");
  hint.className = "accessHint tournamentsHint";

  const layout = document.createElement("div");
  layout.className = "tournamentsLayout";

  const sidePanel = document.createElement("aside");
  sidePanel.className = "tournamentsSidePanel";

  const listMount = document.createElement("div");
  listMount.className = "tournamentsListScroll";

  const detailMount = document.createElement("div");
  detailMount.className = "tournamentsDetail hidden";

  layout.append(sidePanel, listMount);
  shell.append(heading, hint, layout, detailMount);
  root.replaceChildren(shell);

  function stopTimers() {
    if (countdownTimer) clearInterval(countdownTimer);
    if (pollTimer) clearInterval(pollTimer);
    countdownTimer = null;
    pollTimer = null;
  }

  function statusLabel(t: TournamentSummary): string {
    if (t.status === "active") return "LIVE";
    if (t.status === "open") return "FILLING";
    if (t.status === "upcoming") return "UPCOMING";
    return "ENDED";
  }

  function statusClass(t: TournamentSummary): string {
    if (t.status === "active") return "tournamentCard--live";
    if (t.status === "open") return "tournamentCard--filling";
    if (t.status === "upcoming") return "tournamentCard--upcoming";
    return "tournamentCard--ended";
  }

  function cardMeta(t: TournamentSummary): string {
    if (t.status === "upcoming") {
      const left = msUntil(t.startsAt, serverNow);
      return `Starts in ${formatTournamentCountdown(left)}\n${t.registeredCount}/${t.bracketSize} registered`;
    }
    if (t.status === "open") {
      return `Started — waiting for players\n${t.registeredCount}/${t.bracketSize} registered (join until full)`;
    }
    if (t.status === "active") {
      return `${t.registeredCount} players · ${t.bracketSize}-player bracket`;
    }
    return t.winnerName ? `Winner: ${t.winnerName}` : "Completed";
  }

  function canRegister(status: string): boolean {
    return status === "upcoming" || status === "open";
  }

  function orderedList(): TournamentSummary[] {
    return [...lists.active, ...lists.upcoming, ...lists.ended];
  }

  function renderSidePanel() {
    sidePanel.replaceChildren();
    const title = document.createElement("h2");
    title.className = "tournamentsSideTitle";
    title.textContent = "YOUR STATS";
    sidePanel.appendChild(title);

    if (!opts.isSignedIn()) {
      const sign = document.createElement("p");
      sign.className = "accessHint";
      sign.textContent = "Sign in to track registrations and rewards.";
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "btn green";
      btn.textContent = "Sign in";
      btn.onclick = () => opts.onNeedSignIn();
      sidePanel.append(sign, btn);
      return;
    }

    const statsBox = document.createElement("div");
    statsBox.className = "tournamentsSideStats";
    const dotsRow = document.createElement("p");
    dotsRow.className = "tournamentsSideStat";
    dotsRow.innerHTML = `<span>Dots earned</span><strong>${me.dotsEarned.toLocaleString()}</strong>`;
    const winsRow = document.createElement("p");
    winsRow.className = "tournamentsSideStat";
    winsRow.innerHTML = `<span>Tournaments won</span><strong>${me.tournamentsWon}</strong>`;
    statsBox.append(dotsRow, winsRow);
    sidePanel.appendChild(statsBox);

    const regTitle = document.createElement("h3");
    regTitle.className = "tournamentsSideSub";
    regTitle.textContent = "Registered";
    sidePanel.appendChild(regTitle);

    if (!me.registered.length) {
      const empty = document.createElement("p");
      empty.className = "accessHint";
      empty.textContent = "None right now.";
      sidePanel.appendChild(empty);
      return;
    }

    const ul = document.createElement("ul");
    ul.className = "tournamentsSideRegList";
    for (const t of me.registered) {
      const li = document.createElement("li");
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "tournamentsSideRegBtn";
      btn.textContent = `${t.name} · ${statusLabel(t)}`;
      btn.onclick = () => void openDetail(t.id);
      li.appendChild(btn);
      ul.appendChild(li);
    }
    sidePanel.appendChild(ul);
  }

  function renderList() {
    layout.classList.remove("hidden");
    detailMount.classList.add("hidden");
    listMount.replaceChildren();
    const items = orderedList();
    if (!items.length) {
      const empty = document.createElement("p");
      empty.className = "accessHint tournamentsListEmpty";
      empty.textContent = "No tournaments yet.";
      listMount.appendChild(empty);
      return;
    }
    for (const t of items) {
      const card = document.createElement("button");
      card.type = "button";
      card.className = `tournamentCard ${statusClass(t)}`;
      if (t.registered) card.classList.add("tournamentCard--registered");

      const badge = document.createElement("span");
      badge.className = "tournamentCardBadge";
      badge.textContent = statusLabel(t);

      const title = document.createElement("p");
      title.className = "tournamentCardTitle";
      title.textContent = t.name;

      const meta = document.createElement("p");
      meta.className = "tournamentCardMeta";
      meta.textContent = cardMeta(t);

      const size = document.createElement("p");
      size.className = "tournamentCardSize";
      size.textContent = `${t.bracketSize} players`;

      card.append(badge, title, meta, size);
      card.onclick = () => void openDetail(t.id);
      listMount.appendChild(card);
    }
  }

  function renderDetail() {
    if (!detail) return;
    layout.classList.add("hidden");
    detailMount.classList.remove("hidden");
    detailMount.replaceChildren();

    const header = document.createElement("div");
    header.className = "tournamentsDetailHeader";

    const back = document.createElement("button");
    back.type = "button";
    back.className = "btn";
    back.textContent = "← Back";
    back.onclick = () => {
      detail = null;
      render();
    };

    const title = document.createElement("h2");
    title.className = "tournamentsDetailTitle";
    title.textContent = detail.name;

    const statusLine = document.createElement("p");
    statusLine.className = "tournamentsDetailStatus";
    if (detail.status === "upcoming") {
      statusLine.textContent = `Starts in ${formatTournamentCountdown(msUntil(detail.startsAt, serverNow))} · ${detail.registeredCount}/${detail.bracketSize}`;
    } else if (detail.status === "open") {
      statusLine.textContent = `Started — filling roster ${detail.registeredCount}/${detail.bracketSize}`;
    } else if (detail.status === "ended" && detail.winnerName) {
      statusLine.textContent = `Winner: ${detail.winnerName}`;
    } else {
      statusLine.textContent = `${detail.status.toUpperCase()} · ${detail.bracketSize}-player bracket`;
    }

    header.append(back, title, statusLine);
    detailMount.appendChild(header);

    const viewerId = opts.isSignedIn() ? getViewerIdFromDetail(detail) : null;
    const bracket = renderBracketGrid(
      detail.bracketSize,
      detail.matches,
      viewerId,
      detail.roundRewards ?? {},
    );
    bracket.classList.add("tournamentBracketWrap--full");
    detailMount.appendChild(bracket);

    const actions = document.createElement("div");
    actions.className = "tournamentsDetailActions";

    if (canRegister(detail.status)) {
      const regPanel = document.createElement("div");
      regPanel.className = "tournamentsRegPanel";
      const regTitle = document.createElement("h3");
      regTitle.textContent = `Registered (${detail.registrations.length}/${detail.bracketSize})`;
      const ul = document.createElement("ul");
      ul.className = "tournamentRegList";
      for (const r of detail.registrations) {
        const li = document.createElement("li");
        li.textContent = r.displayName || r.userId.slice(0, 8);
        ul.appendChild(li);
      }
      regPanel.append(regTitle, ul);

      if (opts.isSignedIn()) {
        const full = detail.registrations.length >= detail.bracketSize;
        const regBtn = document.createElement("button");
        regBtn.type = "button";
        regBtn.className = "btn green";
        regBtn.textContent = detail.registered ? "Leave" : full ? "Full" : "Register";
        regBtn.disabled = !detail.registered && full;
        regBtn.onclick = async () => {
          try {
            if (detail!.registered) await leaveTournament(detail!.id);
            else await registerTournament(detail!.id);
            await openDetail(detail!.id);
            await refresh();
          } catch (e) {
            hint.textContent = e instanceof Error ? e.message : "Action failed.";
          }
        };
        regPanel.appendChild(regBtn);
      } else {
        const signHint = document.createElement("p");
        signHint.className = "accessHint";
        signHint.textContent = "Sign in to register.";
        regPanel.appendChild(signHint);
      }
      actions.appendChild(regPanel);
    }

    if (detail.status === "active" && opts.isSignedIn()) {
      const myMatch = detail.matches.find(
        (m) =>
          m.viewerSlot > 0 &&
          (m.status === "open" || m.status === "waiting" || m.status === "active"),
      );
      if (myMatch) {
        const me =
          myMatch.viewerSlot === 1 ? myMatch.player1 : myMatch.viewerSlot === 2 ? myMatch.player2 : null;
        const playPanel = document.createElement("div");
        playPanel.className = "tournamentPlayPanel";
        const playHint = document.createElement("p");
        playHint.className = "accessHint";
        if (me?.finished) {
          playHint.textContent = "You finished — waiting for opponent result.";
          playPanel.appendChild(playHint);
        } else {
          if (myMatch.windowEnd) {
            playHint.textContent = `Play window: ${formatTournamentCountdown(msUntil(myMatch.windowEnd, serverNow))} left`;
          } else {
            playHint.textContent = "Join when ready — both players must be in the match.";
          }
          const playBtn = document.createElement("button");
          playBtn.type = "button";
          playBtn.className = "btn green";
          playBtn.textContent = "Play Match";
          playBtn.onclick = () => opts.onPlayMatch(detail!, myMatch.id);
          playPanel.append(playHint, playBtn);
        }
        actions.appendChild(playPanel);
      } else {
        const nextPending = detail.matches.find(
          (m) => m.viewerSlot > 0 && m.status === "pending",
        );
        if (nextPending) {
          const playPanel = document.createElement("div");
          playPanel.className = "tournamentPlayPanel";
          const playHint = document.createElement("p");
          playHint.className = "accessHint";
          playHint.textContent = "Waiting for your next opponent to be decided...";
          playPanel.appendChild(playHint);
          actions.appendChild(playPanel);
        }
      }
    }

    if (actions.childNodes.length) detailMount.appendChild(actions);
  }

  function getViewerIdFromDetail(d: TournamentDetail): string | null {
    const fromOpts = opts.getViewerUserId?.() ?? null;
    if (fromOpts) return fromOpts;
    for (const m of d.matches) {
      if (m.viewerSlot === 1) return m.player1?.userId ?? null;
      if (m.viewerSlot === 2) return m.player2?.userId ?? null;
    }
    return null;
  }

  function render() {
    renderSidePanel();
    if (detail) renderDetail();
    else renderList();
  }

  async function refresh() {
    if (!opts.isSignedIn()) {
      hint.textContent = "Browse tournaments freely — sign in to register and compete.";
    } else {
      hint.textContent = "";
    }
    try {
      const data = await loadTournamentLists();
      lists = data;
      me = data.me ?? { registered: [], dotsEarned: 0, tournamentsWon: 0 };
      serverNow = data.serverNow;
      tickTournamentToasts(lists.upcoming, serverNow);
      render();
    } catch {
      hint.textContent = "Could not load tournaments.";
    }
  }

  async function openDetail(id: string) {
    try {
      detail = await loadTournamentDetail(id);
      serverNow = new Date().toISOString();
      render();
    } catch (e) {
      hint.textContent = e instanceof Error ? e.message : "Load failed.";
    }
  }

  function backToList() {
    detail = null;
    render();
  }

  function handleReturn(): boolean {
    const screen = document.getElementById("tournamentsScreen");
    if (!screen || screen.classList.contains("hidden")) return false;
    if (detail) {
      detail = null;
      render();
      return true;
    }
    return false;
  }

  function isVisible(): boolean {
    const screen = document.getElementById("tournamentsScreen");
    return !!(screen && !screen.classList.contains("hidden"));
  }

  stopTimers();
  countdownTimer = setInterval(() => {
    serverNow = new Date().toISOString();
    if (!detail) renderList();
    else if (detail.status === "upcoming" || detail.status === "open") renderDetail();
    else renderSidePanel();
    tickTournamentToasts(lists.upcoming, serverNow);
  }, 1000);

  pollTimer = setInterval(() => {
    if (detail && (detail.status === "active" || detail.status === "upcoming" || detail.status === "open")) {
      void openDetail(detail.id);
    } else {
      void refresh();
    }
  }, 15000);

  void refresh();

  screenApi = { refresh, openDetail, backToList, handleReturn, isVisible };
  return screenApi;
}

export function showTournamentWaitingScreen(show: boolean, message?: string) {
  let el = document.getElementById("tournamentWaitingScreen");
  if (!el && show) {
    el = document.createElement("div");
    el.id = "tournamentWaitingScreen";
    el.className = "tournamentWaitingScreen";
    el.innerHTML = '<p class="tournamentWaitingText">Waiting for opponent...</p>';
    document.body.appendChild(el);
  }
  const text = el?.querySelector(".tournamentWaitingText");
  if (text && show) {
    text.textContent = message?.trim() || "Waiting for opponent...";
  }
  el?.classList.toggle("hidden", !show);
  document.body.classList.toggle("tournament-waiting", show);
}

export async function tryJoinTournamentMatch(matchId: string): Promise<{
  opponentJoined: boolean;
  opponentName: string | null;
  modeId: string;
  levelIndex: number | null;
}> {
  const data = await joinTournamentMatch(matchId);
  return {
    opponentJoined: !!data.opponentJoined,
    opponentName: typeof data.opponentName === "string" ? data.opponentName : null,
    modeId: String(data.modeId || "normal"),
    levelIndex: data.levelIndex != null ? Number(data.levelIndex) : null,
  };
}
